import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuth } from '../hooks/AuthContext';
import JoinLeagueModal from '../components/JoinLeagueModal';
import JoinPublicLeagueModal from '../components/JoinPublicLeagueModal';

interface FantasyLeague {
  id: number;
  name: string;
  league_type: string;
  live_tournament_id: number;
  tournament_name: string;
  max_participants: number;
  current_participants: number;
  status: string;
  is_public: boolean;
  join_code?: string;
  created_by: number;
  created_at: string;
  is_creator: boolean;
}

const PickemPage: React.FC = () => {
  const [leagues, setLeagues] = useState<FantasyLeague[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showPublicJoinModal, setShowPublicJoinModal] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState<FantasyLeague | null>(null);
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    fetchPickemLeagues();
  }, []);

  const fetchPickemLeagues = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getFantasyLeagues();
      if (data.success) {
        const pickemLeagues = data.tournaments.filter(
          (league: FantasyLeague) => league.league_type === 'pickem'
        );
        setLeagues(pickemLeagues);
      } else {
        setError(data.message || 'Failed to load leagues');
      }
    } catch (err) {
      setError('Failed to load leagues');
      console.error('Error fetching pickem leagues:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLeague = () => {
    if (isLoading) {
      return;
    }
    if (!isAuthenticated) {
      navigate('/tournaments');
      return;
    }
    navigate('/create-league?type=pickem');
  };

  const handleJoinLeague = (league: FantasyLeague) => {
    setSelectedLeague(league);
    
    if (league.is_public) {
      setShowPublicJoinModal(true);
    } else {
      setShowJoinModal(true);
    }
  };

  const handleJoinSuccess = () => {
    fetchPickemLeagues();
  };

  if (loading || isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 text-xl mb-2">⚠️</div>
        <p className="text-white mb-4">{error}</p>
        <button 
          onClick={fetchPickemLeagues}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="text-6xl mb-4">🎯</div>
        <h1 className="text-4xl font-bold text-white mb-4">Pick'em League Tournaments</h1>
        <p className="text-xl text-white/80 max-w-3xl mx-auto">
          Predict match outcomes and earn points based on accuracy! Make your picks for match winners, 
          total runs, and player of the match to climb the leaderboard.
        </p>
      </div>

      {/* How It Works */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20 mb-8">
        <h2 className="text-2xl font-bold text-white mb-6">How Pick'em League Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="bg-purple-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🎯</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">1. Make Predictions</h3>
            <p className="text-white/70">Predict match winners, total runs, and player of the match for upcoming matches.</p>
          </div>
          <div className="text-center">
            <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📊</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">2. Earn Points</h3>
            <p className="text-white/70">Score points based on prediction accuracy. More accurate predictions = more points!</p>
          </div>
          <div className="text-center">
            <div className="bg-green-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🏆</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">3. Compete & Win</h3>
            <p className="text-white/70">Compete against other players in your league. The most accurate predictor wins!</p>
          </div>
        </div>
      </div>

      {/* Key Features */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20 mb-8">
        <h2 className="text-2xl font-bold text-white mb-6">Key Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Prediction Types</h3>
            <ul className="space-y-2 text-white/80">
              <li>• <strong>Match Winner:</strong> Predict which team will win the match</li>
              <li>• <strong>Total Runs:</strong> Predict the total runs scored in the match</li>
              <li>• <strong>Player of the Match:</strong> Predict who will be named MoM</li>
              <li>• <strong>Multiple Predictions:</strong> Make predictions for each match</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">League Management</h3>
            <ul className="space-y-2 text-white/80">
              <li>• <strong>Public & Private:</strong> Join public leagues or create private ones</li>
              <li>• <strong>Join Codes:</strong> Easy sharing with friends via join codes</li>
              <li>• <strong>Real-time Updates:</strong> Live standings and results</li>
              <li>• <strong>Tournament Support:</strong> Multiple tournaments supported</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Scoring System */}
      <div className="bg-white/10 backdrop-blur-md rounded-lg p-8 border border-white/20 mb-8">
        <h2 className="text-2xl font-bold text-white mb-6">Scoring System</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Match Winner Prediction</h3>
            <ul className="space-y-2 text-white/80">
              <li>• <strong>Correct Prediction:</strong> 10 points</li>
              <li>• <strong>Wrong Prediction:</strong> 0 points</li>
              <li>• <strong>No Prediction:</strong> 0 points</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">Enhanced Predictions</h3>
            <ul className="space-y-2 text-white/80">
              <li>• <strong>Total Runs (Exact):</strong> 20 points</li>
              <li>• <strong>Total Runs (Close):</strong> 10 points</li>
              <li>• <strong>Player of the Match:</strong> 15 points</li>
              <li>• <strong>Bonus Points:</strong> Additional rewards for accuracy</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
        <button
          onClick={handleCreateLeague}
          className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-3 rounded-lg text-lg font-semibold transition-all transform hover:scale-105"
        >
          Create Pick'em League
        </button>
        <Link
          to="/tournaments"
          className="bg-white/10 hover:bg-white/20 text-white px-8 py-3 rounded-lg text-lg font-semibold border border-white/20 transition-all text-center"
        >
          Browse All Tournaments
        </Link>
      </div>

      {/* Join League Modal for Private Leagues */}
      <JoinLeagueModal
        isOpen={showJoinModal}
        onClose={() => {
          setShowJoinModal(false);
          setSelectedLeague(null);
        }}
        onSuccess={handleJoinSuccess}
        leagueId={selectedLeague?.id}
        isPublicLeague={false}
        leagueType="pickem"
      />

      {/* Join Public League Modal */}
      {selectedLeague && (
        <JoinPublicLeagueModal
          isOpen={showPublicJoinModal}
          onClose={() => {
            setShowPublicJoinModal(false);
            setSelectedLeague(null);
          }}
          onSuccess={handleJoinSuccess}
          leagueId={selectedLeague.id}
          leagueName={selectedLeague.name}
          currentParticipants={selectedLeague.current_participants}
          maxParticipants={selectedLeague.max_participants}
        />
      )}
    </div>
  );
};

export default PickemPage; 